import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApplicationStorageService } from './application-storage.service';
import { User } from '../models/user';
import { UserRequest } from '../models/userrequest';
import { UserLogin } from '../../Common/models/user.login';

@Injectable({
    providedIn: 'root'
})
export class UserService {

    user: User = new User();
    currentUser: BehaviorSubject<any> = new BehaviorSubject(null);
    currentUser$ = this.currentUser.asObservable();

    constructor(private httpClient: HttpClient,
        private applicationStorageService: ApplicationStorageService
    ) { }

    getUser(): Observable<User> {

        const userDetails =
            this.applicationStorageService.getUserDetails() as User;

        // First use the RetailHub user already stored during login
        if (userDetails) {
            this.currentUser.next(userDetails);
            this.user = userDetails;

            return of(userDetails);
        }

        // Azure AD mode:
        // Get the logged-in RetailHub user from the new /user/me endpoint
        if (environment.authentication.useAzureAd) {

            return this.httpClient
                .get<User>(`${environment.gateway_url}/user/me`)
                .pipe(
                    map(item => {
                        this.currentUser.next(item);
                        this.user = item;

                        this.applicationStorageService
                            .setUserDetails(item);

                        return item;
                    })
                );
        }

        // Existing custom-login flow - keep for fallback
        const url = `${environment.gateway_url}/User`;

        return this.httpClient.get<User>(url).pipe(
            map(item => {
                this.currentUser.next(item);
                this.user = item;

                this.applicationStorageService
                    .setUserDetails(this.user);

                return item;
            })
        );
    }

    getITAdminUsers() {
        const url = `${environment.gateway_url}/User/itusers`
        return this.httpClient.get<any[]>(url).pipe(
            map(item => {
                return item;
            })
        );
    }
    getUsers(): Observable<User[]> {
        const url = `${environment.gateway_url}/User/users`
        return this.httpClient.get<any[]>(url).pipe(
            map(items => {
                return items;
            })
        );
    }

    getUsersByCategories(categoryIds: number[]): Observable<User[]> {
        return this.httpClient.post<User[]>(environment.gateway_url + '/GetUsersByCategories', categoryIds);
    }

    getUserDetails(id: any): Observable<User[]> {
        const url = `${environment.gateway_url}/User/userdetails/${id}`
        return this.httpClient.get<any[]>(url).pipe(
            map(items => {
                return items;
            })
        );
    }
    getUserRequestsByRegion(userId: any, regionId: any): Observable<User[]> {
        const url = `${environment.gateway_url}/User/userrequestsbyregion/${userId}/${regionId}`
        return this.httpClient.get<any[]>(url).pipe(
            map(items => {
                return items;
            })
        );
    }
    downloadExcel(regionId: number): Observable<any> {
        const url = `${environment.gateway_url}/User/downloadExcel`
        return this.httpClient.post(url, regionId, { responseType: 'blob' });
    }

    saveUserRequest(requestDTO: any[]): Observable<any> {
        const url = `${environment.gateway_url}/User/saveuserrequest`
        return this.httpClient.post(url, requestDTO).pipe(
            map(item => item ? item : [])
        );
    }
    updateUserStatus(userId: any, regionId: any): Observable<User[]> {
        const url = `${environment.gateway_url}/User/updateUserStatus/${userId}/${regionId}`
        return this.httpClient.get<any[]>(url).pipe(
            map(items => {
                return items;
            })
        );
    }

    storeToken(token: string): Observable<any> {
        const url = `${environment.gateway_url}/TokenManage/storeToken?token=${token}`
        return this.httpClient.get<any>(url).pipe(
            map(items => {
                return items;
            })
        );
    }
    getAllUsers(): Observable<User[]> {
        const url = `${environment.gateway_url}/getallusers`
        return this.httpClient.get<any[]>(url).pipe(
            map(items => {
                return items;
            })
        );
    }

    getAllUsersWithRoles(): Observable<User[]> {
        const url = `${environment.gateway_url}/getalluserswithroles`
        return this.httpClient.get<any[]>(url).pipe(
            map(items => {
                return items;
            })
        );
    }

    updateApprovalOrUserStatus(userDTO: User): Observable<User> {
        const url = `${environment.gateway_url}/updateapprovaloruserstatus`
        return this.httpClient.post(url, userDTO).pipe(
            map(item => item ? item : [])
        );
    }

    getUserRoleInfo(userId: number): Observable<UserLogin> {
        const url = `${environment.gateway_url}/getuserroleinfobyuserid/${userId}`
        return this.httpClient.get<UserLogin>(url).pipe(
            map(items => {
                return items;
            })
        );
    }

    //Delete User
    deleteUser(userId?: number): Observable<any> {
        const url = `${environment.gateway_url}/deleteuser/${userId}`;
        return this.httpClient.delete(url).pipe(
            map(item => item ? item : [])
        );
    }

    //Users List
    GetUsersListReport(userIds: number[]): Observable<any> {
        const url = `${environment.gateway_url}/exportuserslist`;

        return this.httpClient.post(url, userIds, {
            responseType: 'blob',
            observe: 'response'
        }).pipe(
            map((res: HttpResponse<Blob>) => {
                const blob = new Blob([res.body!], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }
}
